/**
 * I testi della pagina /servizi, dal modello `uploads/servizi 01 oi.html`
 * e dalla tabella del §5.6 di `uploads/oi-istruzioni-servizi 02.md`
 * (16/10/2026): aree, frasi in rame, titoli, descrizioni e indirizzi dei
 * quattordici servizi, copiati tali e quali.
 *
 * Dal 17/10/2026 la pagina è la versione «Parti dalla domanda»
 * (`uploads/servizi rivisitata.html` + `uploads/oi-istruzioni-servizi
 * rivisitata.md`): ogni area è una domanda del lettore, e qui accanto ai
 * testi di prima stanno i nuovi, parola per parola dal modello (domanda,
 * descrizione, scritta d'angolo, parola della mappa, collegati, «Ricevi»).
 *
 * Sono testi della pagina e non del servizio, per questo non stanno in
 * `lib/services.ts` (che serve le pagine /servizi/[slug]) né in
 * `vociCatalogo()` di `lib/serviziCatalogo.ts`, che altre pagine leggono
 * ancora con le formule di prima.
 *
 * Modulo senza direttiva: lo leggono la pagina e i pezzi delle pagine dei
 * servizi, che sono Server Components, e potrebbe leggerlo un Client
 * Component (lo era il filtro delle aree, tolto il 17/10/2026) (una costante
 * esportata da un file "use client" e letta dal server non è il suo
 * valore: vedi LEAD_DISPACCI nei learnings).
 */

import type { NomeIcona } from "@/components/home/Icona";

export type ServizioPagina = {
  /** «01»…«14», il numero del piede «Servizio 01 di 14». */
  numero: string;
  slug: string;
  /** La frase breve in rame sopra il titolo. */
  frase: string;
  titolo: string;
  descrizione: string;
  icona: NomeIcona;
  /** «Ricevi»: che cosa riceve il cliente (§ 5.1 di `oi-istruzioni-servizi
   *  rivisitata.md`). Le voci di 05, 07, 11 e 13 sono scritte dall'autore
   *  delle istruzioni partendo dalla pagina del servizio, e vanno
   *  confermate; le altre sono prese da quella pagina. */
  ricevi: string;
};

export type AreaPagina = {
  /** L'ancora della domanda, la stessa delle sezioni di prima
   *  (`#a-info`…): un vecchio `/servizi#a-ris` apre la domanda 3. */
  id: string;
  nome: string;
  icona: NomeIcona;
  /** La domanda del lettore che l'area risolve (*mio* nelle istruzioni). */
  domanda: string;
  /** La descrizione dell'area sotto la domanda, Gelasio 19 blu. Quelle di
   *  «Informazione e analisi» e «Organizzazione e operatività» sono del
   *  sito (erano le tessere d'area), le altre due *mio*. */
  descrizione: string;
  /** La scritta nell'angolo della rete d'apertura. */
  angolo: string;
  /** La parola chiave della mappa delle domande, in fondo all'indice. */
  parola: string;
  /** «Collegati»: i servizi di altre domande che servono anche a questa,
   *  nell'ordine della tabella del § 3.2. */
  collegati: string[];
  servizi: ServizioPagina[];
};

export const AREE_PAGINA: AreaPagina[] = [
  {
    id: "a-info",
    nome: "Informazione e analisi",
    icona: "notizia",
    domanda: "Devo capire che cosa sta succedendo.",
    descrizione: "Leggere il contesto, verificare le fonti, valutare una prospettiva.",
    angolo: "Capire che cosa succede",
    parola: "Capire",
    collegati: ["rischio-reputazionale", "pianificazione-strategica"],
    servizi: [
      {
        numero: "01",
        slug: "monitoraggio",
        frase: "Seguire ciò che cambia",
        titolo: "Monitoraggio e analisi settoriale",
        descrizione:
          "Osservazione continuativa di temi, settori e filiere rilevanti per la tua attività.",
        icona: "segnale",
        ricevi:
          "Una segnalazione mirata quando serve attenzione e, con cadenza concordata, un report sul quadro d’insieme.",
      },
      {
        numero: "02",
        slug: "report",
        frase: "Approfondire una decisione",
        titolo: "Report geostrategici",
        descrizione:
          "Analisi contestualizzate per leggere attori, interessi e relazioni in uno scenario.",
        icona: "report",
        ricevi:
          "Un dossier riservato con executive summary, analisi e allegati, e un briefing di 60 minuti.",
      },
      {
        numero: "03",
        slug: "verifica-informativa",
        frase: "Verificare prima di agire",
        titolo: "Verifica informativa",
        descrizione:
          "Riscontro delle informazioni e delle fonti alla base di una valutazione.",
        icona: "focus",
        ricevi:
          "Un dossier firmato con esito puntuale: confermato, smentito o incerto.",
      },
      {
        numero: "04",
        slug: "analisi",
        frase: "Valutare una prospettiva",
        titolo: "Studi di mercato e fattibilità",
        descrizione:
          "Lettura del mercato e degli elementi utili a valutare la fattibilità di un progetto.",
        icona: "scenari",
        ricevi:
          "Un dossier con executive summary, scenari e una raccomandazione firmabile.",
      },
    ],
  },
  {
    id: "a-mer",
    nome: "Mercati e presenza estera",
    icona: "esteri",
    domanda: "Voglio lavorare su un mercato estero.",
    descrizione: "Preparare e presidiare l’attività fuori dall’Italia: la presenza, gli scambi, i fornitori.",
    angolo: "Lavorare all’estero",
    parola: "Estero",
    collegati: ["sanzioni", "due-diligence", "analisi"],
    servizi: [
      {
        numero: "05",
        slug: "presenza-internazionale",
        frase: "Costruire una presenza",
        titolo: "Espansione e presenza all’estero",
        descrizione:
          "Supporto alla preparazione e al presidio della tua attività sui mercati esteri.",
        icona: "esteri",
        ricevi:
          "Contatti verificati, carte in regola e una prima lettura del paese e dei suoi codici.",
      },
      {
        numero: "06",
        slug: "import-export",
        frase: "Preparare gli scambi",
        titolo: "Import / Export",
        descrizione:
          "Un quadro di partner, costi e condizioni operative per le attività commerciali estere.",
        icona: "commercio",
        ricevi:
          "Una selezione ragionata delle controparti e una valutazione del costo complessivo.",
      },
      {
        numero: "07",
        slug: "materie-prime",
        frase: "Individuare i fornitori",
        titolo: "Ricerca e approvvigionamento materie prime",
        descrizione:
          "Supporto alla ricerca di controparti e alle esigenze di approvvigionamento.",
        icona: "agricoltura",
        ricevi:
          "Fornitori valutati, condizioni e percorso fino a destinazione, poi la fornitura seguita nel tempo.",
      },
    ],
  },
  {
    id: "a-ris",
    nome: "Controparti e rischio",
    icona: "sanzioni",
    domanda: "Devo fidarmi di una controparte.",
    descrizione: "Sapere chi si ha di fronte e quali vincoli pesano su relazioni e operazioni.",
    angolo: "Fidarsi di una controparte",
    parola: "Controparti",
    collegati: ["verifica-informativa", "monitoraggio", "giuridico"],
    servizi: [
      {
        numero: "08",
        slug: "due-diligence",
        frase: "Conoscere la controparte",
        titolo: "Due diligence strategica",
        descrizione: "Approfondimento della controparte e del contesto in cui opera.",
        icona: "azienda",
        ricevi:
          "Un fascicolo sulla controparte, letto per capitoli, che si chiude con un giudizio.",
      },
      {
        numero: "09",
        slug: "rischio-reputazionale",
        frase: "Comprendere la propria esposizione",
        titolo: "Rischio reputazionale",
        descrizione:
          "Lettura delle informazioni presenti in rete e dei possibili elementi di criticità.",
        icona: "crisi",
        ricevi:
          "Una mappa dei temi critici, il monitoraggio e un piano di risposta.",
      },
      {
        numero: "10",
        slug: "sanzioni",
        frase: "Orientarsi tra i vincoli",
        titolo: "Gestione sanzioni",
        descrizione:
          "Supporto alla valutazione delle restrizioni rilevanti per relazioni e operazioni.",
        icona: "sanzioni",
        ricevi:
          "La mappatura dei collegamenti e la documentazione degli elementi emersi e dei punti da approfondire.",
      },
    ],
  },
  {
    id: "a-org",
    nome: "Organizzazione e operatività",
    icona: "organizzazione",
    domanda: "Devo mettere ordine nell’impresa.",
    descrizione: "Dare ordine alle informazioni, ai processi e agli obiettivi dell’impresa.",
    angolo: "Mettere ordine nell’impresa",
    parola: "Ordine",
    collegati: ["report", "analisi"],
    servizi: [
      {
        numero: "11",
        slug: "cibernetica-aziendale",
        frase: "Dare ordine alle informazioni",
        titolo: "Cibernetica aziendale",
        descrizione:
          "Analisi delle relazioni tra informazioni, processi e decisioni nell’impresa.",
        icona: "organizzazione",
        ricevi:
          "La mappa dei processi, un team formato a leggere i dati e un desk operativo.",
      },
      {
        numero: "12",
        slug: "informatica",
        frase: "Collegare strumenti e processi",
        titolo: "IT e digitalizzazione",
        descrizione:
          "Supporto alle esigenze di infrastruttura informatica, dati e sicurezza.",
        icona: "tecnologia",
        ricevi:
          "Un quadro dell’esistente e delle priorità, poi un piano di assistenza con livelli di servizio concordati.",
      },
      {
        numero: "13",
        slug: "giuridico",
        frase: "Affrontare le questioni operative",
        titolo: "Consulenza legale",
        descrizione:
          "Supporto sulle esigenze contrattuali, di conformità e di contenzioso.",
        icona: "istituzione",
        ricevi:
          "La documentazione rilevante, le opzioni e le loro conseguenze, per decidere.",
      },
      {
        numero: "14",
        slug: "pianificazione-strategica",
        frase: "Tradurre gli obiettivi in un piano",
        titolo: "Pianificazione strategica",
        descrizione:
          "Affiancamento nell’analisi, nell’elaborazione e nel conseguimento degli obiettivi strategici dell’impresa.",
        icona: "impresa",
        ricevi:
          "Un piano operativo con responsabilità, tappe e indicatori.",
      },
    ],
  },
];

export const TOTALE_SERVIZI = AREE_PAGINA.reduce((n, a) => n + a.servizi.length, 0);
